/**
 * LA BURBUJA DE UN ECHO SIN TEXTO (coexistence C3d, 27-oct-2026).
 *
 * Cuando el titular manda desde su app WhatsApp Business algo que no es texto —una nota de voz, una
 * foto, un documento—, `functions/integrations/coexistence/echoes.js` lo guarda en el chat como
 * mensaje del negocio con el MISMO marcador que el entrante (`[audio]`, `[image] <caption>`) y, en
 * `meta`, `origen: 'app_business'`, `tipoWhatsApp` (el `type` de Meta) y `objetoDeMeta` (el objeto
 * del tipo, entero: id, mime_type, caption, filename…). El binario NO se descarga: vive en el
 * teléfono del negocio, no en Storage, y no hay nada que firmar ni reproducir.
 *
 * Hasta este commit el inbox pintaba ese documento por la rama de texto: la burbuja decía `[audio]`
 * en inglés, sin ícono y sin decir de dónde venía (revisión independiente de C3b, 🟠3). Decisión de
 * Rodrigo (27-oct): una burbuja propia —ícono por tipo y texto traducido—, sin reproductor ni
 * descarga, y cualquier otro mensaje exactamente igual que hoy.
 *
 * ⚠️ LA REGLA ES LA MISMA que `esEchoSinTexto` en `functions/bot/utils/historialParaElModelo.js`
 * (lo que el modelo describe en vez de calcar es lo que el operador ve con burbuja propia). Las dos
 * copias se comparan por conducta en `functions/__tests__/inboxEchoSinTexto.jest.test.js`, que
 * ejecuta esta fuente con `vm` sobre documentos que deja el escritor real.
 *
 * El caption y el nombre salen de `meta.objetoDeMeta`, NUNCA de parsear `[image] …` del texto: un
 * regex sobre un dato guardado es la clase de parseo que rompe en silencio (CuerpoMensaje, 24-sep).
 */

export const ORIGEN_APP_BUSINESS = 'app_business';

/** ¿Es un mensaje del titular, mandado desde la app Business, que NO es texto? Pura. */
export function esEchoSinTexto(msg) {
  return msg?.meta?.origen === ORIGEN_APP_BUSINESS
    && typeof msg.meta.tipoWhatsApp === 'string' && msg.meta.tipoWhatsApp !== 'text';
}

/**
 * Ícono y clave i18n por `type` de Meta. `voice` es la nota de voz de la app (misma burbuja que
 * `audio`). Un tipo que no esté aquí —`unsupported`, una edición, lo que Meta invente— cae a la
 * burbuja genérica: se dice que el equipo mandó algo desde la app, sin adivinar qué.
 */
export const BURBUJA_POR_TIPO = Object.freeze({
  audio: { icono: '🎤', clave: 'detail.echoAudio' },
  voice: { icono: '🎤', clave: 'detail.echoAudio' },
  image: { icono: '📷', clave: 'detail.echoImage' },
  video: { icono: '🎬', clave: 'detail.echoVideo' },
  document: { icono: '📄', clave: 'detail.echoDocument' },
  sticker: { icono: '🏷️', clave: 'detail.echoSticker' },
  contacts: { icono: '👤', clave: 'detail.echoContact' },
  location: { icono: '📍', clave: 'detail.echoLocation' },
});
export const BURBUJA_GENERICA = Object.freeze({ icono: '📱', clave: 'detail.echoGeneric' });

const cadena = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * Los tipos cuyo CONTENIDO se quedó en el teléfono del negocio (un binario que no se descargó). Una
 * ubicación o un contacto NO: su dato viene entero en `objetoDeMeta` y se pinta aquí, así que el pie
 * «se ve en el teléfono» sería falso (revisión independiente de C3d, 🟠1).
 */
const TIPOS_CON_DATO_COMPLETO = Object.freeze(['location', 'contacts']);

/**
 * Lo que pinta la burbuja de un echo sin texto. Pura; sólo tiene sentido si `esEchoSinTexto(msg)`.
 * @returns {{ tipo: string, icono: string, clave: string, nombre: string|null, caption: string|null, soloEnElTelefono: boolean }}
 *   `nombre`: el filename del documento, el nombre/dirección Y las coordenadas de la ubicación, o el
 *     nombre del contacto (con «+N» si vienen varios).
 *   `caption`: el texto que el titular escribió con la foto, el vídeo o el documento.
 *   `soloEnElTelefono`: si el contenido no está aquí (media binaria) y hay que decirlo.
 */
export function burbujaDeEcho(msg) {
  const tipo = msg.meta.tipoWhatsApp;
  // `hasOwn` y no `[tipo]` a secas: un `type` como `constructor` o `toString` recorrería el prototipo.
  const { icono, clave } = Object.hasOwn(BURBUJA_POR_TIPO, tipo) ? BURBUJA_POR_TIPO[tipo] : BURBUJA_GENERICA;
  const obj = msg.meta.objetoDeMeta;
  let nombre = null;
  if (obj && typeof obj === 'object') {
    if (tipo === 'document') nombre = cadena(obj.filename);
    else if (tipo === 'location') {
      // Las coordenadas SIEMPRE: al compartir «mi ubicación actual» no hay nombre ni dirección, y
      // antes de esta burbuja el operador las veía en el texto (`📍 … (lat, lng)`, el escritor).
      const coords = Number.isFinite(obj.latitude) && Number.isFinite(obj.longitude) ? `(${obj.latitude}, ${obj.longitude})` : null;
      nombre = [cadena(obj.name), cadena(obj.address), coords].filter(Boolean).join(' · ') || null;
    } else if (tipo === 'contacts' && Array.isArray(obj)) {
      const primero = cadena(obj[0]?.name?.formatted_name);
      nombre = primero && obj.length > 1 ? `${primero} +${obj.length - 1}` : primero;
    }
  }
  const caption = obj && typeof obj === 'object' && !Array.isArray(obj) ? cadena(obj.caption) : null;
  return { tipo, icono, clave, nombre, caption, soloEnElTelefono: !TIPOS_CON_DATO_COMPLETO.includes(tipo) };
}
